import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, BookOpen, Eye, X, ChevronDown, Download } from 'lucide-react';
import ReportService from '../services/ReportService';
import Table from '../components/ui/Table';
import Modal from '../components/ui/Modal';
import SearchableDropdown from '../components/SearchableDropdown';
import { useToast } from '../contexts/ToastContext';
import { useSelector } from 'react-redux';
import { homeworkService } from '../services/homeworkService';
import { classService } from '../services/classService';
import CustomDatePicker from '../components/ui/CustomDatePicker';

import { useNavigate } from 'react-router-dom';

const Homeworks = () => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { user } = useSelector((state) => state.auth);
  const [homeworks, setHomeworks] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentHomeworkId, setCurrentHomeworkId] = useState(null);
  const [selectedClassFilter, setSelectedClassFilter] = useState('');
  const [selectedAcademicYearFilter, setSelectedAcademicYearFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    dueDate: '',
    classId: '',
    academicYear: '',
    staffId: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, [user.token]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [homeworksData, classesData, teachersData] = await Promise.all([
        homeworkService.getAllHomeworks(user.token),
        classService.getAllClasses(user.token),
        axios.get('/api/admin/getAllTeachers', { headers: { Authorization: `Bearer ${user.token}` } }).then(res => res.data.teachers || [])
      ]);
      setHomeworks(homeworksData);
      setClasses(classesData);
      setTeachers(teachersData || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // No auto-initialization - show all homework by default

  const teacherOptions = (teachers || []).map(t => ({
    ...t,
    label: `${t.name} (${t.employeeId || 'No ID'})`
  }));

  const handleClassSelection = (selectedClass) => {
    setFormData({ ...formData, classId: selectedClass ? selectedClass._id : '' });
  };

  const handleTeacherSelection = (selectedTeacher) => {
    setFormData({ ...formData, staffId: selectedTeacher ? selectedTeacher._id : '' });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      dueDate: '',
      classId: '',
      academicYear: '',
      staffId: ''
    });
    setIsEditing(false);
    setCurrentHomeworkId(null);
  };

  const handleEdit = (homework) => {
    setFormData({
      name: homework.name,
      description: homework.description,
      dueDate: homework.dueDate ? new Date(homework.dueDate).toISOString().split('T')[0] : '',
      classId: homework.classId,
      academicYear: homework.academicYear || '',
      staffId: homework.staffId || ''
    });
    setCurrentHomeworkId(homework._id);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this homework?')) {
      try {
        const response = await homeworkService.deleteHomework(id, user.token);
        if (response.success) {
          success('Homework deleted successfully');
          fetchInitialData();
        } else {
          error(response.message || 'Failed to delete homework');
        }
      } catch (err) {
        console.error('Error deleting homework:', err);
        error('Failed to delete homework');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.description || !formData.dueDate || !formData.classId || !formData.academicYear) {
      error('Please fill in all required fields');
      return;
    }

    try {
      let response;
      if (isEditing) {
        response = await homeworkService.updateHomework(currentHomeworkId, formData, user.token);
      } else {
        response = await homeworkService.createHomework(formData, user.token);
      }

      if (response.success) {
        success(isEditing ? 'Homework updated successfully' : 'Homework created successfully');
        setIsModalOpen(false);
        resetForm();
        fetchInitialData();
      } else {
        error(response.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      error('Operation failed');
    }
  };

  // Filter homeworks
  const filteredHomeworks = homeworks.filter(homework => {
    const matchesSearch = !searchTerm ||
      homework.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      homework.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesClass = !selectedClassFilter ||
      (homework.class && homework.class._id === selectedClassFilter);

    const matchesAcademicYear = !selectedAcademicYearFilter ||
      homework.academicYear === selectedAcademicYearFilter;

    // Date Range Filter (based on Due Date)
    let matchesDateRange = true;
    if (startDateFilter || endDateFilter) {
      const dueDate = new Date(homework.dueDate);
      dueDate.setHours(0, 0, 0, 0);

      if (startDateFilter) {
        const start = new Date(startDateFilter);
        start.setHours(0, 0, 0, 0);
        if (dueDate < start) matchesDateRange = false;
      }

      if (matchesDateRange && endDateFilter) {
        const end = new Date(endDateFilter);
        end.setHours(0, 0, 0, 0);
        if (dueDate > end) matchesDateRange = false;
      }
    }

    return matchesSearch && matchesClass && matchesAcademicYear && matchesDateRange;
  });

  // Pagination
  const totalPages = Math.ceil(filteredHomeworks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedHomeworks = filteredHomeworks.slice(startIndex, startIndex + itemsPerPage);

  const columns = [
    {
      key: 'name',
      label: 'Assignment',
      render: (item) => (
        <div>
          <p className="font-medium text-gray-900">{item.name}</p>
          <p className="text-sm text-gray-500 truncate max-w-xs">{item.description}</p>
        </div>
      )
    },
    {
      key: 'class',
      label: 'Class',
      render: (item) => (
        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
          {item.class ? `${item.class.name} - ${item.class.section}` : 'N/A'}
        </span>
      )
    },
    {
      key: 'academicYear',
      label: 'Academic Year',
      render: (item) => (
        <span className="text-sm text-gray-600">
          {item.academicYear || 'N/A'}
        </span>
      )
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: (item) => (
        <span className={`text-sm ${new Date(item.dueDate) < new Date() ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
          {new Date(item.dueDate).toLocaleDateString()}
        </span>
      )
    },
    {
      key: 'assignedAt',
      label: 'Assigned Date',
      render: (item) => (
        <span className="text-sm text-gray-600">
          {new Date(item.assignedAt).toLocaleDateString()}
        </span>
      )
    },
    {
      key: 'staff',
      label: 'Staff',
      render: (item) => (
        <span className="text-sm font-medium text-slate-600">
          {item.staff?.name || 'N/A'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/homeworks/${item._id}`)}
            className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
            title="View Details"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => handleEdit(item)}
            className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
            title="Edit"
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => handleDelete(item._id)}
            className="p-1 text-gray-600 hover:text-red-600 transition-colors"
            title="Delete"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  // Filter classes based on academic year
  const filteredClasses = classes.filter(cls => {
    if (!formData.academicYear) return false;

    // Convert class academic year timestamps to years for comparison
    // Assuming academicYearFrom is a timestamp
    const classYearFrom = new Date(cls.academicYearFrom).getFullYear();
    const classYearTo = new Date(cls.academicYearTo).getFullYear();
    const classAcademicYear = `${classYearFrom}-${classYearTo}`;

    return classAcademicYear === formData.academicYear;
  });

  const classOptions = filteredClasses.map(cls => ({
    _id: cls._id,
    name: `${cls.name} - ${cls.section}`,
    subtitle: `Capacity: ${cls.class_capacity}`
  }));

  // Get unique academic years from classes
  const getUniqueAcademicYears = () => {
    const academicYears = new Set();
    classes.forEach(cls => {
      if (cls.academicYearFrom && cls.academicYearTo) {
        const fromYear = cls.academicYearFrom.toString().length <= 4 
          ? cls.academicYearFrom 
          : new Date(cls.academicYearFrom).getFullYear();
        const toYear = cls.academicYearTo.toString().length <= 4 
          ? cls.academicYearTo 
          : new Date(cls.academicYearTo).getFullYear();
        academicYears.add(`${fromYear}-${toYear}`);
      }
    });
    return Array.from(academicYears).sort();
  };

  const academicYearOptions = getUniqueAcademicYears();

  // Filter classes for dropdown based on selected academic year
  const dropdownClasses = classes.filter(cls => {
    if (!selectedAcademicYearFilter) return true;

    if (cls.academicYearFrom && cls.academicYearTo) {
      const fromYear = cls.academicYearFrom.toString().length <= 4 
        ? cls.academicYearFrom 
        : new Date(cls.academicYearFrom).getFullYear();
      const toYear = cls.academicYearTo.toString().length <= 4 
        ? cls.academicYearTo 
        : new Date(cls.academicYearTo).getFullYear();
      const academicYear = `${fromYear}-${toYear}`;
      return academicYear === selectedAcademicYearFilter;
    }
    return false;
  });

  const dropdownClassOptions = dropdownClasses.map(cls => ({
    _id: cls._id,
    name: `${cls.name} - ${cls.section}`,
    subtitle: `Capacity: ${cls.class_capacity}`
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto">
      {/* Modern Header Section */}
      <div className="glass-card rounded-[2.5rem] p-8 px-10 flex flex-col md:flex-row justify-between items-center gap-6 premium-shadow">
        <div className="text-center md:text-left">
          <h1 className="text-4xl font-black tracking-tight text-[#1e293b]">
            <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">Homework</span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium flex items-center justify-center md:justify-start gap-2">
            <BookOpen size={18} className="text-[#1B6CA8]" />
            Manage assignments and homework
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          <div className="flex bg-slate-100 rounded-2xl p-1 shadow-sm">
            <button
              onClick={() => {
                const columns = [
                  { header: 'Title', dataKey: 'name' },
                  { header: 'Class', dataKey: 'className' },
                  { header: 'Section', dataKey: 'section' },
                  { header: 'Due Date', dataKey: 'dueDate' },
                  { header: 'Staff', dataKey: 'staff' }
                ];
                const data = homeworks.map(h => ({
                  name: h.name,
                  className: h.class?.name || 'N/A',
                  section: h.class?.section || 'N/A',
                  dueDate: new Date(h.dueDate).toLocaleDateString(),
                  staff: h.staff?.name || 'N/A'
                }));
                ReportService.generatePDF('Homework Assignments', columns, data, 'homework_assignments.pdf');
              }}
              className="px-4 py-2.5 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
            >
              <Download size={16} className="text-blue-600" />
              <span>PDF</span>
            </button>
            <button
              onClick={() => {
                const data = homeworks.map(h => ({
                  Title: h.name,
                  Description: h.description,
                  Class: h.class?.name || 'N/A',
                  Section: h.class?.section || 'N/A',
                  'Due Date': new Date(h.dueDate).toLocaleDateString(),
                  'Assigned At': new Date(h.assignedAt).toLocaleDateString(),
                  Staff: h.staff?.name || 'N/A'
                }));
                ReportService.generateExcel(data, 'homework_assignments.xlsx');
              }}
              className="px-4 py-2.5 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
            >
              <Download size={16} className="text-emerald-600" />
              <span>Excel</span>
            </button>
          </div>
          <button
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            className="primary-gradient-bg text-white px-8 py-3.5 rounded-2xl hover:opacity-90 transition-all duration-300 flex items-center gap-3 font-bold shadow-lg shadow-blue-200/50 hover:scale-[1.02] active:scale-[0.98]"
          >
            <Plus size={24} />
            <span>Add Homework</span>
          </button>
        </div>
      </div>

        {/* Filters */}
        <div className="glass-card rounded-[2rem] p-8 premium-shadow border-none animate-slideInUp">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 gap-4">
            <div>
              <h3 className="text-xl font-black text-slate-800 tracking-tight">Filters</h3>
              <p className="text-slate-400 text-sm font-medium">Refine your homework assignments view</p>
            </div>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedClassFilter('');
                setSelectedAcademicYearFilter('');
                setStartDateFilter('');
                setEndDateFilter('');
              }}
              className="text-sm font-bold text-blue-600 px-4 py-2 bg-blue-50 rounded-full hover:bg-blue-100 transition-all duration-300 flex items-center gap-2"
            >
              <X size={16} />
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Search */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Search Assignment</label>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all duration-300 font-semibold text-slate-700"
              />
            </div>
            {/* Academic Year */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Academic Year</label>
              <div className="relative">
                <select
                  value={selectedAcademicYearFilter}
                  onChange={(e) => {
                    setSelectedAcademicYearFilter(e.target.value);
                    setSelectedClassFilter(''); // Reset class when year changes
                  }}
                  className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all duration-300 font-semibold text-slate-700 cursor-pointer appearance-none"
                >
                  <option value="">All Years</option>
                  {academicYearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
              </div>
            </div>

            {/* Class */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Class</label>
              <SearchableDropdown
                options={dropdownClassOptions}
                value={dropdownClassOptions.find(opt => opt._id === selectedClassFilter) || ''}
                onChange={(val) => setSelectedClassFilter(val ? val._id : '')}
                placeholder={selectedAcademicYearFilter ? "Class" : "All Classes"}
                label=""
                searchPlaceholder="Search classes..."
              />
            </div>

            {/* Start Date */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Due Date From</label>
              <CustomDatePicker
                label=""
                name="startDateFilter"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                className="w-full"
              />
            </div>

            {/* End Date */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Due Date Due</label>
              <CustomDatePicker
                label=""
                name="endDateFilter"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </div>

      <Table
        data={paginatedHomeworks}
        columns={columns}
        searchable={false}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        emptyMessage="No homework assignments found"
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={filteredHomeworks.length}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={isEditing ? "Edit Homework" : "Add New Homework"}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Assignment Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="e.g. Math Chapter 5 Exercises"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              rows={4}
              placeholder="Detailed instructions for the assignment..."
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year *</label>
              <div className="relative">
                <select
                  value={formData.academicYear}
                  onChange={(e) => {
                    setFormData({ ...formData, academicYear: e.target.value, classId: '' });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white cursor-pointer appearance-none"
                  required
                >
                  <option value="">Select Academic Year</option>
                  {academicYearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Class *</label>
              <SearchableDropdown
                options={classOptions}
                value={classOptions.find(opt => opt._id === formData.classId) || ''}
                onChange={handleClassSelection}
                placeholder={formData.academicYear ? "Select Class" : "Select Academic Year First"}
                label=""
                searchPlaceholder="Search classes..."
                required
                disabled={!formData.academicYear}
              />
              {!formData.academicYear && (
                <p className="text-xs text-gray-500 mt-1">Please select an academic year to see available classes</p>
              )}
            </div>
            <div>
              <CustomDatePicker
                label="Due Date"
                name="dueDate"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                required={true}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Assign Teacher</label>
              <SearchableDropdown
                options={teacherOptions}
                value={teacherOptions.find(opt => opt._id === formData.staffId) || ''}
                onChange={(selected) => setFormData({ ...formData, staffId: selected?._id || '' })}
                placeholder="Select Teacher (Optional)"
                label=""
                searchPlaceholder="Search teachers..."
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {isEditing ? 'Update Homework' : 'Add Homework'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Homeworks;
